import React, { useEffect, useState, useCallback } from "react";
import PortalShell from "@/components/PortalShell";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Clock, MapPin, LogIn, LogOut, User, Send, AlertCircle, CheckCircle2, PlayCircle } from "lucide-react";

function useGeo() {
  const [state, setState] = useState({ status: "idle", geo: null });
  const capture = useCallback(() => new Promise((resolve) => {
    if (!("geolocation" in navigator)) { setState({ status: "unsupported", geo: null }); return resolve(null); }
    setState({ status: "capturing", geo: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const geo = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
        setState({ status: "ok", geo });
        resolve(geo);
      },
      () => { setState({ status: "denied", geo: null }); resolve(null); },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  }), []);
  return { ...state, capture };
}

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return <span className="tabular-nums">{now.toLocaleTimeString()}</span>;
}

function formatDur(iso) {
  const d = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  const h = Math.floor(d / 3600), m = Math.floor((d % 3600) / 60), s = Math.floor(d % 60);
  return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
}

function LiveDuration({ startIso }) {
  const [, setT] = useState(0);
  useEffect(() => { const i = setInterval(() => setT((n) => n + 1), 1000); return () => clearInterval(i); }, []);
  return <span className="tabular-nums">{formatDur(startIso)}</span>;
}

export default function TimeClock() {
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [pickClient, setPickClient] = useState("");
  const [pickService, setPickService] = useState("");
  const [busy, setBusy] = useState(false);
  const [showReq, setShowReq] = useState(false);
  const [req, setReq] = useState({ punch_id: "", explanation: "", requested_clock_in: "", requested_clock_out: "" });
  const geo = useGeo();

  const load = async () => {
    try {
      const [meRes, listRes] = await Promise.all([
        api.get("/clock/me"),
        api.get("/clock/my-clients"),
      ]);
      setData(meRes.data);
      setClients(listRes.data.clients);
      setServices(listRes.data.service_types);
      if (!pickClient && listRes.data.clients[0]) setPickClient(listRes.data.clients[0].id);
      if (!pickService && listRes.data.service_types[0]) setPickService(listRes.data.service_types[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const punchIn = async () => {
    if (!pickClient || !pickService) { toast.error("Select a client and service type."); return; }
    setBusy(true);
    const g = await geo.capture();
    try {
      await api.post("/clock/punch-in", { client_id: pickClient, service_type_id: pickService, geo: g });
      toast.success("Clocked in");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const punchOut = async () => {
    setBusy(true);
    const g = await geo.capture();
    try {
      await api.post("/clock/punch-out", { geo: g });
      toast.success("Clocked out");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submitCorrection = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/clock/correction-request", {
        punch_id: req.punch_id || null,
        explanation: req.explanation,
        requested_clock_in: req.requested_clock_in || null,
        requested_clock_out: req.requested_clock_out || null,
      });
      toast.success("Correction request sent to your administrator");
      setShowReq(false);
      setReq({ punch_id: "", explanation: "", requested_clock_in: "", requested_clock_out: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <PortalShell variant="employee"><div className="text-slate-500 text-sm">Loading…</div></PortalShell>;

  const open = data.open_punch;
  const clockedIn = Boolean(open);

  return (
    <PortalShell variant="employee">
      <Toaster richColors position="top-center" />

      <div className="max-w-md md:max-w-2xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-orange font-bold">Time Clock</div>
            <h1 className="mt-1 font-outfit text-3xl md:text-4xl font-semibold text-brand-navy">Hi, {data.user.name.split(" ")[0]}.</h1>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Now</div>
            <div className="font-outfit text-lg font-semibold text-brand-navy"><LiveClock /></div>
          </div>
        </div>

        {/* Status card */}
        <div className={`mt-6 rounded-3xl p-6 border ${clockedIn ? "bg-brand-navy text-white border-brand-navy" : "bg-white border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"}`} data-testid="clock-status-card">
          {clockedIn ? (
            <>
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-brand-orange animate-pulse"/>
                <div className="text-xs uppercase tracking-widest text-orange-200 font-bold">Clocked in</div>
              </div>
              <div className="mt-3 font-outfit text-4xl md:text-5xl font-semibold" data-testid="clock-elapsed">
                <LiveDuration startIso={open.clock_in}/>
              </div>
              <div className="mt-2 text-sm text-white/70 flex items-center gap-2">
                <User className="w-4 h-4"/> {open.client_name || "—"} · <span className="text-orange-200">{open.service_type_name || ""}</span>
              </div>
              <div className="mt-1 text-xs text-white/60 flex items-center gap-1"><Clock className="w-3 h-3"/> since {new Date(open.clock_in).toLocaleString()}</div>
              <button disabled={busy} onClick={punchOut} data-testid="punch-out-btn"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange text-white hover:brightness-95 py-4 font-semibold text-base disabled:opacity-60"
                style={{ transition: "filter 200ms ease" }}>
                <LogOut className="w-5 h-5"/> Clock Out
              </button>
            </>
          ) : (
            <>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Ready to work</div>
              <div className="mt-2 font-outfit text-3xl font-semibold text-brand-navy">Start your shift</div>

              <div className="mt-5 space-y-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Client</label>
                  <select data-testid="pick-client" value={pickClient} onChange={(e)=>setPickClient(e.target.value)}
                    className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-orange">
                    <option value="">Select a client…</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.reference ? ` (${c.reference})` : ""}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Service type</label>
                  <select data-testid="pick-service" value={pickService} onChange={(e)=>setPickService(e.target.value)}
                    className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-orange">
                    <option value="">Select a service…</option>
                    {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>

              <button disabled={busy || !pickClient || !pickService} onClick={punchIn} data-testid="punch-in-btn"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy text-white hover:bg-brand-navydark py-4 font-semibold text-base disabled:opacity-60"
                style={{ transition: "background-color 200ms ease" }}>
                <LogIn className="w-5 h-5"/> Clock In
              </button>
            </>
          )}
          <div className="mt-4 text-[11px] text-white/60 flex items-center gap-1">
            <MapPin className="w-3 h-3"/>
            {geo.status === "ok" && "Location captured"}
            {geo.status === "capturing" && "Getting location…"}
            {geo.status === "denied" && "Location denied (admin will review)"}
            {geo.status === "unsupported" && "Location not supported"}
            {geo.status === "idle" && "Location captured on punch"}
          </div>
        </div>

        {/* Pay period card */}
        <div className="mt-6 wc-card p-6 flex items-center justify-between" data-testid="pay-period-card">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Current pay period</div>
            <div className="mt-1 font-outfit text-lg font-semibold text-brand-navy">{data.current_pay_period ? data.current_pay_period.label : "Not set"}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Hours</div>
            <div className="mt-1 font-outfit text-3xl font-semibold text-brand-orange" data-testid="pay-period-hours">{data.hours_this_period}</div>
          </div>
        </div>

        {/* Recent shifts */}
        <div className="mt-6 wc-card p-6" data-testid="recent-shifts-card">
          <div className="flex items-center justify-between">
            <h3 className="font-outfit text-lg font-semibold text-brand-navy">Recent shifts</h3>
            <button onClick={() => setShowReq(true)} className="text-xs font-semibold text-brand-orange hover:brightness-90 inline-flex items-center gap-1" data-testid="open-correction-btn">
              <Send className="w-3.5 h-3.5"/> Submit correction
            </button>
          </div>
          <div className="mt-4 divide-y divide-slate-100">
            {data.recent_punches.length === 0 && <div className="text-sm text-slate-500 py-4">No shifts yet.</div>}
            {data.recent_punches.map((p) => (
              <div key={p.id} className="py-3 flex items-center gap-4" data-testid={`shift-row-${p.id}`}>
                <div className={`w-9 h-9 rounded-xl grid place-items-center ${p.clock_out ? "bg-emerald-50 text-emerald-600" : "bg-orange-50 text-brand-orange"}`}>
                  {p.clock_out ? <CheckCircle2 className="w-4 h-4"/> : <PlayCircle className="w-4 h-4"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-brand-navy">{new Date(p.clock_in).toLocaleDateString()} · <span className="text-slate-500 font-normal">{new Date(p.clock_in).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} → {p.clock_out ? new Date(p.clock_out).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "…"}</span></div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                    <User className="w-3 h-3"/> {p.client_id ? clients.find(c => c.id === p.client_id)?.name || "Client" : "—"}
                    · {services.find(s => s.id === p.service_type_id)?.name || "—"}
                  </div>
                  {p.flags && p.flags.length > 0 && (
                    <div className="mt-1 flex gap-1 flex-wrap">
                      {p.flags.map((f) => (
                        <span key={f} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 font-semibold uppercase tracking-wider">
                          <AlertCircle className="w-2.5 h-2.5"/> {f.replace("_"," ")}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-brand-navy tabular-nums">{p.hours.toFixed(2)}h</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Correction request modal */}
      {showReq && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-6" data-testid="correction-modal">
          <form onSubmit={submitCorrection} className="w-full max-w-md bg-white rounded-3xl p-6">
            <h3 className="font-outfit text-xl font-semibold text-brand-navy">Correction request</h3>
            <p className="text-xs text-slate-500 mt-1">Your admin will review. Punches cannot be changed silently.</p>

            <div className="mt-4">
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Which shift?</label>
              <select value={req.punch_id} onChange={(e)=>setReq({...req, punch_id: e.target.value})} data-testid="req-punch"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm">
                <option value="">Missing shift (create new)</option>
                {data.recent_punches.map((p) => (
                  <option key={p.id} value={p.id}>{new Date(p.clock_in).toLocaleString()} ({p.hours.toFixed(2)}h)</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Correct clock-in</label>
                <input type="datetime-local" value={req.requested_clock_in} onChange={(e)=>setReq({...req, requested_clock_in: e.target.value ? new Date(e.target.value).toISOString() : ""})} data-testid="req-in"
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"/>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Correct clock-out</label>
                <input type="datetime-local" value={req.requested_clock_out} onChange={(e)=>setReq({...req, requested_clock_out: e.target.value ? new Date(e.target.value).toISOString() : ""})} data-testid="req-out"
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"/>
              </div>
            </div>

            <div className="mt-3">
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Explanation</label>
              <textarea required rows={3} value={req.explanation} onChange={(e)=>setReq({...req, explanation: e.target.value})} data-testid="req-explanation"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm"/>
            </div>

            <div className="mt-5 flex gap-3 justify-end">
              <button type="button" onClick={() => setShowReq(false)} className="wc-btn-outline" data-testid="req-cancel">Cancel</button>
              <button type="submit" disabled={busy} className="wc-btn-primary disabled:opacity-60" data-testid="req-submit">
                {busy ? "Sending…" : "Send request"}
              </button>
            </div>
          </form>
        </div>
      )}
    </PortalShell>
  );
}
